import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, FileText, Send, UserCheck, AlertTriangle, UploadCloud,
  Clock, CheckCircle2, ArrowRight, Scale, Gauge, KeyRound, HeartHandshake, ChevronDown, Smartphone,
  HandCoins, FileCheck2,
} from "lucide-react";
import { LOGO_URL, FLYER_URL, BRAND, TAGLINE, SLOGAN } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08 } }),
};

const NAV = [
  { label: "Statutory Engine", href: "#engine" },
  { label: "Workflow", href: "#workflow" },
  { label: "Letters", href: "#letters" },
  { label: "Compare", href: "#compare" },
  { label: "Scan", href: "#scan" },
  { label: "FAQ", href: "#faq" },
];

const TICKER = [
  "FCRA § 605B — 4-Business-Day Mandatory Block",
  "FCRA § 623 — Furnisher Must Cease Re-Reporting",
  "FTC Identity Theft Report Required",
  "USPS Certified Mail Lifecycle Tracking",
  "CFPB Escalation Ready",
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = () => navigate(user ? "/app" : "/login");

  return (
    <div className="min-h-screen text-neutral-900 overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "glass" : "bg-transparent"
        }`}
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="brand-logo">
            <img src={LOGO_URL} alt={BRAND} className="h-11 w-auto object-contain" />
          </a>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-sm text-neutral-600 hover:text-[#C1121F] transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/login")}
              className="text-neutral-800 hover:text-[#C1121F] hover:bg-neutral-100"
              data-testid="nav-signin-button"
            >
              Sign In
            </Button>
            <Button
              onClick={go}
              className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold shadow-lg shadow-red-900/30"
              data-testid="nav-launch-button"
            >
              Launch CRM <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="absolute inset-0 grid-texture opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div initial="hidden" animate="show" variants={fade} className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#C1121F]/40 bg-[#C1121F]/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-[#9B0E18]">
                <Scale className="h-3.5 w-3.5" /> FCRA § 605B · 15 U.S.C. § 1681c-2 Compliant Engine
              </span>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Crush fraudulent accounts in <span className="gold-text">4 business days</span>, not thirty.
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-neutral-600 max-w-xl">
                {BRAND} is the identity-theft dispute command center — a built-in consumer CRM,
                credit-report matcher, ready-to-mail statutory letter studio, and certified-mail tracking,
                all in one place.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button
                  onClick={go}
                  size="lg"
                  className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold h-12 px-7 text-base shadow-xl shadow-red-900/30"
                  data-testid="hero-cta-button"
                >
                  Start Disputing Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => document.getElementById("letters")?.scrollIntoView({ behavior: "smooth" })}
                  size="lg"
                  variant="outline"
                  className="rounded-full h-12 px-7 text-base border-neutral-200 bg-neutral-50 hover:bg-neutral-200 text-neutral-900"
                  data-testid="hero-secondary-button"
                >
                  See Letter Templates
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-neutral-500">
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#15803D]" /> Big-3 bureaus + furnishers</span>
                <span className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#15803D]" /> Audit-ready packets</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="glass rounded-3xl p-3 shadow-2xl shadow-red-900/10">
                <div className="rounded-2xl bg-white p-4 border border-neutral-200">
                  <img src={FLYER_URL} alt={`${BRAND} flyer`} className="w-full mx-auto object-contain rounded-lg" />
                </div>
                <div className="grid grid-cols-3 gap-2 p-3">
                  {[
                    { icon: Gauge, label: "Improve Score" },
                    { icon: FileText, label: "Dispute Fraud" },
                    { icon: KeyRound, label: "Unlock Credit" },
                  ].map((s, i) => (
                    <div key={i} className="rounded-xl bg-neutral-50 border border-neutral-200 p-3 text-center">
                      <s.icon className="h-5 w-5 mx-auto text-[#C1121F]" />
                      <div className="mt-1.5 text-[11px] text-neutral-600">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ticker */}
      <div className="border-y border-neutral-200 bg-[#F6EEEE]/60 py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-ticker">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-neutral-500">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C1121F]" /> {t}
            </span>
          ))}
        </div>
      </div>

      {/* Service pillars */}
      <section id="pillars" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="What we do" title="Credit today. Opportunities tomorrow." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { icon: Gauge, t: "Improve Your Score", d: "Track every bureau score over time and watch it climb as fraud clears." },
              { icon: FileCheck2, t: "Dispute Inaccuracies", d: "Challenge fraudulent and inaccurate tradelines with statutory letters." },
              { icon: HandCoins, t: "Build Financial Health", d: "Turn a cleaner report into stronger, lasting financial footing." },
              { icon: KeyRound, t: "Unlock Opportunities", d: "Open the door to better rates, approvals and the home you want." },
            ].map((p, i) => (
              <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="group rounded-2xl border border-neutral-200 bg-white p-7 text-center hover:border-[#C1121F]/40 hover:shadow-lg hover:shadow-red-900/5 hover:-translate-y-0.5 transition-[border-color,transform,box-shadow] duration-200" data-testid={`landing-pillar-${i}`}>
                <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-[#C1121F]/10 mx-auto">
                  <p.icon className="h-7 w-7 text-[#C1121F]" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-neutral-900">{p.t}</h3>
                <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Features bento */}
      <section id="engine" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="The Command Center" title="Everything a dispute case needs, in one workspace" />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-12">
            <FeatureCard className="md:col-span-7" icon={UploadCloud} title="Smart Credit-Report Ingestion & Matching"
              body="Drag-and-drop PDF, image, or CSV credit reports. We match each file to the right consumer and auto-extract fraudulent tradelines — creditor, account, bureau, balance and fraud reason." accent="#15803D" />
            <FeatureCard className="md:col-span-5" icon={UserCheck} title="Built-in Consumer CRM"
              body="Track every client with identity verification, FTC & police report numbers, matched reports and full dispute history." accent="#C1121F" />
            <FeatureCard className="md:col-span-5" icon={FileText} title="Ready-to-Mail Letter Studio"
              body="Pre-built statutory templates for each dispute type. Live preview, print, and one-click PDF." accent="#C1121F" />
            <FeatureCard className="md:col-span-7" icon={Clock} title="Certified-Mail Tracking & SLA Countdown"
              body="Follow every dispute from Drafted → Dispatched → Under 4-Day Review → Blocked. Statutory deadline clocks keep bureaus accountable." accent="#15803D" />
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="py-16 md:py-24 border-y border-neutral-200 bg-[#F6EEEE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="How it works" title="From stolen identity to blocked tradeline" />
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { icon: UserCheck, t: "Add the consumer", d: "Capture identity, FTC report and contact details in the CRM." },
              { icon: UploadCloud, t: "Upload the report", d: "Match a credit report and auto-extract fraudulent accounts." },
              { icon: Send, t: "Generate & mail", d: "Produce statutory § 605B / § 623 letters, print or export PDF." },
              { icon: CheckCircle2, t: "Track the block", d: "Log certified mail and watch the 4-day clock to resolution." },
            ].map((s, i) => (
              <motion.div key={i} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="relative rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-6">
                <span className="absolute -top-3 -left-3 h-9 w-9 rounded-full bg-[#C1121F] text-white font-bold grid place-items-center text-sm">{i + 1}</span>
                <s.icon className="h-7 w-7 text-[#C1121F]" />
                <h4 className="mt-4 font-display font-semibold text-lg">{s.t}</h4>
                <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Letters */}
      <section id="letters" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead align="left" eyebrow="Letter Studio" title="Verified templates, ready to dispatch" />
            <ul className="mt-8 space-y-4">
              {[
                "FCRA § 605B Bureau Identity Theft Block (Equifax · Experian · TransUnion)",
                "FCRA § 623 Direct Furnisher Fraud Dispute",
                "4-Day Statutory Demand for non-compliant bureaus",
                "Unauthorized Inquiry Removal Demand",
                "Identity Theft Affidavit + FTC / Police report cover sheet",
              ].map((l, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#15803D]/15 grid place-items-center">
                    <CheckCircle2 className="h-4 w-4 text-[#15803D]" />
                  </span>
                  <span className="text-neutral-600">{l}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white text-[#111] p-8 shadow-2xl shadow-red-900/10 font-mono text-[12px] leading-relaxed">
            <div className="inline-block border border-black px-2 py-1 text-[10px] font-bold mb-4">CERTIFIED MAIL — RETURN RECEIPT REQUESTED</div>
            <p className="font-bold">RE: REQUEST FOR BLOCK OF FRAUDULENT INFORMATION — FCRA § 605B</p>
            <p className="mt-3 text-slate-700">Equifax Information Services LLC<br/>Fraud & Identity Theft Department<br/>P.O. Box 740256, Atlanta, GA 30374-0256</p>
            <p className="mt-3 text-slate-700">To Whom It May Concern: I am a victim of identity theft. Pursuant to 15 U.S.C. § 1681c-2 you are required to block the reporting of information resulting from identity theft within four business days…</p>
            <p className="mt-3 text-neutral-400">• Creditor: First National Synthetic Bank | Acct #: XXXX-4491 | $12,400</p>
          </div>
        </div>
      </section>

      {/* Compare */}
      <section id="compare" className="py-16 md:py-24 border-y border-neutral-200 bg-[#F6EEEE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Why statutory blocks win" title="Traditional disputes vs. the § 605B block" />
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/60 p-8">
              <div className="flex items-center gap-2 text-neutral-500"><AlertTriangle className="h-5 w-5" /> <span className="font-semibold">Traditional 30-day reinvestigation</span></div>
              <ul className="mt-5 space-y-3 text-sm text-neutral-500">
                <li>· Up to 30–45 days of vague "reinvestigation"</li>
                <li>· Fraud can be re-reported by furnishers</li>
                <li>· No statutory deadline leverage</li>
                <li>· Manual, scattered paperwork</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-[#C1121F]/40 bg-[#C1121F]/[0.06] p-8">
              <div className="flex items-center gap-2 text-[#9B0E18]"><ShieldCheck className="h-5 w-5" /> <span className="font-semibold">{BRAND} § 605B block</span></div>
              <ul className="mt-5 space-y-3 text-sm text-neutral-800">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#15803D] mt-0.5" /> 4-business-day mandatory block</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#15803D] mt-0.5" /> Furnishers ordered to cease re-reporting</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#15803D] mt-0.5" /> Deadline countdown + CFPB escalation</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#15803D] mt-0.5" /> One audit-ready packet per case</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Questions" title="What you need to know" />
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {[
              ["Do I need a police report or FTC report?", "An FTC Identity Theft Report is required for a § 605B block. A police report strengthens the file and is required by some furnishers. Both fields are tracked per consumer in the CRM."],
              ["Why certified mail?", "Certified Mail with Return Receipt creates a legal record of delivery, which starts the statutory clock and preserves evidence for CFPB escalation."],
              ["Which credit reports can I upload?", "PDF reports, images (JPG/PNG), and CSV exports. The system matches each report to a consumer and extracts fraudulent tradelines automatically."],
              ["Can multiple clients be managed?", "Yes. The CRM is built for both self-filers and agencies managing many consumers, each with isolated data."],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-neutral-200">
                <AccordionTrigger className="text-left hover:text-[#C1121F] hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="text-neutral-500">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Scan to start */}
      <section id="scan" className="py-16 md:py-24 border-y border-neutral-200 bg-[#F6EEEE]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHead align="left" eyebrow="Scan &amp; Go" title="Scan to start your dispute" />
            <p className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed">
              Saw us on a flyer? Point your phone camera at the code to open Love Financial Solutions and
              begin challenging fraudulent and inaccurate accounts in minutes.
            </p>
            <ul className="mt-8 space-y-3">
              {["Open the site instantly — no app to install", "Create your secure account", "Upload your report and start disputing"].map((l, i) => (
                <li key={i} className="flex items-start gap-3 text-neutral-600">
                  <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#15803D]/15 grid place-items-center"><CheckCircle2 className="h-4 w-4 text-[#15803D]" /></span>{l}
                </li>
              ))}
            </ul>
            <Button onClick={go} size="lg" className="mt-8 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold h-12 px-7" data-testid="scan-cta-button">
              Or start here <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
          <div className="flex justify-center">
            <div className="rounded-3xl bg-white p-6 shadow-2xl shadow-red-900/10 text-center max-w-xs">
              <img src="/lfs-qr.png" alt="Scan to visit lovefinancialsolutions.com" className="w-64 h-64 mx-auto object-contain" data-testid="landing-qr-image" />
              <div className="mt-4 font-display font-bold text-neutral-900 flex items-center justify-center gap-2"><Smartphone className="h-4 w-4" /> Scan with your camera</div>
              <div className="mt-1 text-xs font-mono text-neutral-400">lovefinancialsolutions.com</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-[#C1121F]/30 bg-gradient-to-br from-[#7A0B13] to-[#C1121F] p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 grid-texture opacity-30" />
            <div className="relative">
              <HeartHandshake className="h-10 w-10 mx-auto text-[#C1121F]" />
              <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold">Ready to protect your consumers?</h2>
              <p className="mt-3 text-neutral-600">{SLOGAN}</p>
              <Button onClick={go} size="lg"
                className="mt-8 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold h-12 px-8 text-base"
                data-testid="cta-launch-button">
                Launch the CRM <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-neutral-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND} className="h-10 w-auto object-contain" />
          </div>
          <p className="text-xs text-neutral-400 max-w-md text-center md:text-right">
            <a href="/terms" className="text-[#C1121F] hover:text-[#9B0E18] font-medium">Terms &amp; Conditions</a> · Educational dispute-management software. Not legal advice. Results are not guaranteed. FCRA citations provided for reference.
          </p>
        </div>
      </footer>
    </div>
  );
}

function SectionHead({ eyebrow, title, align = "center" }) {
  return (
    <div className={align === "center" ? "text-center max-w-2xl mx-auto" : "max-w-xl"}>
      <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#C1121F]">{eyebrow}</span>
      <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{title}</h2>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body, accent, className = "" }) {
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
      className={`rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-7 hover:border-[#C1121F]/40 hover:-translate-y-0.5 transition-[border-color,transform] duration-200 ${className}`}>
      <span className="inline-grid place-items-center h-11 w-11 rounded-xl" style={{ backgroundColor: `${accent}1f` }}>
        <Icon className="h-5.5 w-5.5" style={{ color: accent }} />
      </span>
      <h3 className="mt-5 font-display text-xl font-semibold">{title}</h3>
      <p className="mt-2.5 text-neutral-500 leading-relaxed">{body}</p>
    </motion.div>
  );
}
